<template>
    <div>
        <Card class="products-card">
            <div class="products-header">
                <div class="title-section">
                    <h2>All Products <span class="product-count">(Total: {{ totalResults }})</span></h2>
                </div>
                <div class="action-buttons">
                    <AllowPermission :permission="PERMISSION_CODES.CMS_PRINT_LABEL">
                        <Button
                            noMargin
                            @click="$router.push(ROUTE_PATHS.PRINT_LABELS)"
                            class="btn-secondary"
                            title="Print Labels"
                        >
                            <i class="material-icons">print</i> Print Labels
                        </Button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.CMS_PRODUCT_IMPORT">
                        <Button
                            noMargin
                            @click="$router.push(ROUTE_PATHS.PRODUCTS_BULK_IMPORT)"
                            class="btn-secondary"
                            title="Import Products"
                        >
                            <i class="material-icons">upload_file</i> Bulk Import
                        </Button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_PRODUCT">
                        <Button
                            noMargin
                            @click="$router.push(ROUTE_PATHS.PRODUCTS_CREATE)"
                            class="btn-primary"
                            title="Create a New Product"
                        >
                            <i class="material-icons">add</i> Create Product
                        </Button>
                    </AllowPermission>
                </div>
            </div>

            <div class="products-content">
                <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_PRODUCT">
                    <!-- Compact Filter Bar -->
                    <div class="compact-filters-bar">
                        <!-- Search -->
                        <div class="filter-item search-item">
                            <div class="input-with-icon">
                                <i class="material-icons search-icon">search</i>
                                <input
                                    v-model="searchStr"
                                    type="text"
                                    class="form-control search-input"
                                    placeholder="Search products..."
                                    aria-label="Search products"
                                />
                                <button
                                    v-if="searchStr"
                                    type="button"
                                    class="clear-search"
                                    @click="searchStr = ''"
                                    aria-label="Clear product search"
                                >
                                    <i class="material-icons">close</i>
                                </button>
                            </div>
                        </div>

                        <!-- Categories Dropdown -->
                        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_CATEGORY">
                            <div class="filter-item category-filter">
                                <select
                                    v-model="selectedCategory"
                                    @change="applyFilters"
                                    class="form-select"
                                    aria-label="Filter by category"
                                >
                                    <option value="">All Categories</option>
                                    <option v-for="itemGroup of itemGroups"
                                            :key="itemGroup.id"
                                            :value="itemGroup.name">
                                        {{ itemGroup.name }}
                                    </option>
                                </select>
                            </div>
                        </AllowPermission>

                        <!-- View Mode -->
                        <div class="filter-item view-filter">
                            <select
                                v-model="viewMode"
                                @change="toggleViewMode"
                                class="form-select"
                                aria-label="Choose product view"
                            >
                                <option value="products">Products</option>
                                <option value="variations">Variations</option>
                            </select>
                        </div>

                        <!-- Sort By -->
                        <div class="filter-item sort-filter">
                            <select
                                v-model="sortBy"
                                @change="applyFilters"
                                class="form-select"
                                aria-label="Sort products"
                            >
                                <option value="dateAdded">Newest First</option>
                                <option value="dateAddedOld">Oldest First</option>
                                <option value="alphabetical">A-Z</option>
                                <option value="alphabeticalDesc">Z-A</option>
                            </select>
                        </div>

                        <!-- Secondary Filters -->
                        <div class="more-filters-wrapper" ref="moreFilters">
                            <button
                                type="button"
                                class="btn-more-filters"
                                :class="{ active: showMoreFilters || activeAdvancedFilterCount }"
                                :aria-expanded="showMoreFilters ? 'true' : 'false'"
                                aria-controls="product-more-filters"
                                @click="toggleMoreFilters"
                            >
                                <i class="material-icons">tune</i>
                                <span>More filters</span>
                                <span v-if="activeAdvancedFilterCount" class="filter-count">
                                    {{ activeAdvancedFilterCount }}
                                </span>
                                <i class="material-icons chevron">
                                    {{ showMoreFilters ? "expand_less" : "expand_more" }}
                                </i>
                            </button>
                            <div
                                v-if="showMoreFilters"
                                id="product-more-filters"
                                class="more-filters-popover"
                                @keydown.esc="showMoreFilters = false"
                            >
                                <div class="filter-item">
                                    <label for="product-manufacturer-filter">Manufacturer</label>
                                    <select
                                        id="product-manufacturer-filter"
                                        v-model="selectedManufacturer"
                                        @change="applyFilters"
                                        class="form-select"
                                    >
                                        <option value="">All Manufacturers</option>
                                        <option v-for="manufacturer in manufacturers"
                                                :key="manufacturer"
                                                :value="manufacturer">
                                            {{ manufacturer }}
                                        </option>
                                    </select>
                                </div>

                                <div class="filter-item">
                                    <label for="product-supplier-filter">Supplier</label>
                                    <select
                                        id="product-supplier-filter"
                                        v-model="selectedSupplier"
                                        @change="applyFilters"
                                        class="form-select"
                                    >
                                        <option value="">All Suppliers</option>
                                        <option v-for="supplier in suppliers"
                                                :key="supplier"
                                                :value="supplier">
                                            {{ supplier }}
                                        </option>
                                    </select>
                                </div>

                                <div class="filter-item">
                                    <label for="product-status-filter">Status</label>
                                    <select
                                        id="product-status-filter"
                                        v-model="selectedStatus"
                                        @change="applyFilters"
                                        class="form-select"
                                    >
                                        <option v-for="status in statusOptions"
                                                :key="status.value"
                                                :value="status.value">
                                            {{ status.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <!-- Reset Button -->
                        <div v-if="hasActiveFilters" class="filter-item reset-filter">
                            <button type="button" @click="resetFilters" class="btn-reset">
                                <i class="material-icons">refresh</i>
                                <span class="reset-text">Reset</span>
                            </button>
                        </div>

                        <!-- Loading indicator -->
                        <div v-if="loadingFilters" class="filter-item">
                            <div class="filter-loading">
                                <i class="material-icons spinning">sync</i>
                            </div>
                        </div>
                    </div>

                    <div v-if="viewMode === 'variations' && canEditProducts" class="variation-tools-shell">
                        <label class="select-page-control">
                            <input type="checkbox" :checked="allVisibleVariationsSelected" @change="toggleSelectVisible" />
                            <span>Select visible variations</span>
                        </label>
                        <VariationBulkActions
                            :selected-items="selectedVariations"
                            :item-groups="itemGroups"
                            :can-create="canCreateProducts"
                            @clear="clearVariationSelection"
                            @completed="handleVariationOperationCompleted"
                        />
                    </div>

                        <UiState v-if="loading" type="loading" title="Loading products" />
                        <UiState
                            v-else-if="loadError"
                            type="error"
                            :message="loadError"
                            actionLabel="Try again"
                            @action="fetchProducts"
                        />

                    <div class="item-container" v-else>
                        <UiState
                            v-if="filteredItems.length == 0"
                            type="empty"
                            title="No products found"
                            message="Try changing your search or filter criteria."
                        />
                        <div v-else>
                            <div class="product-items-grid">
                                <div v-for="item of filteredItems" :key="item.id" class="product-list-item-wrapper">
                                    <ProductListItem
                                        :item="item"
                                        :selectable="viewMode === 'variations' && canEditProducts"
                                        :selected="selectedVariationIds.includes(item.id)"
                                        @toggle-selection="toggleVariationSelection"
                                    />
                                </div>
                            </div>
                            <div class="pagination-container">
                                <div class="pagination-info">
                                    Page {{ page }} of {{ totalPages }}
                                </div>
                                <Paginator :totalPages="totalPages" :page="page" @change="page = $event" />
                            </div>
                        </div>
                    </div>
                </AllowPermission>
            </div>
        </Card>
    </div>
</template>

<script>
import CrudIndex from "../../components/crud/index.vue"
import { createField, getServerAssetUrl, createColumn } from "../../utils/common"
import { http } from "@/utils/http"
import Paginator from "../../components/other/Paginator.vue"
import Button from "../../components/UI/Button.vue"
import ProductListItem from "../../components/product/ProductListItem.vue"
import VariationBulkActions from "../../components/product/VariationBulkActions.vue"
import Card from "../../components/UI/Card.vue"
import UiState from "../../components/UI/UiState.vue"
import { mapActions, mapState } from "vuex"

import AllowPermission from "../../components/layout/AllowPermission.vue"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { ROUTE_PATHS } from "@/router/route-paths"

export default {
    data() {
        return {
            items: [],
            loading: false,
            loadError: "",
            itemGroups: [],
            selectedCategory: "",
            searchStr: "",
            totalPages: 0,
            page: 1,
            totalResults: "...",
            limit: 50,
            PERMISSION_CODES,
            ROUTE_PATHS,
            searchDebounceTimer: null,

            // New filter properties
            selectedManufacturer: "",
            selectedSupplier: "",
            selectedStatus: "",
            viewMode: "products", // "products" or "variations"

            // New sorting properties
            sortBy: "dateAdded", // "dateAdded", "alphabetical", "dateAddedOld", "alphabeticalDesc"
            sortOrder: "desc", // "asc", "desc"

            // Filter options from API
            manufacturers: [],
            suppliers: [],
            statusOptions: [
                { value: "", label: "All Statuses" },
                { value: "active", label: "Active" },
                { value: "coming_soon", label: "Coming Soon" },
                { value: "inactive", label: "Inactive" },
                { value: "discontinued", label: "Discontinued" },
                { value: "archived", label: "Archived" }
            ],

            // Loading states for filter options
            loadingFilters: false,
            showMoreFilters: false,
            selectedVariationIds: [],
        }
    },
    components: {
        CrudIndex,
        Card,
        Paginator,
        Button,
        ProductListItem,
        VariationBulkActions,
        UiState,
        AllowPermission,
    },
    async mounted() {
        document.addEventListener("click", this.handleDocumentClick)
        this.setData({
            pageTitle: "Products",
        })
        await this.fetchCategories()
        await this.fetchFilterOptions()
        await this.fetchProducts()
    },
    beforeDestroy() {
        // Cleanup debounce timer
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
        }
        document.removeEventListener("click", this.handleDocumentClick)
    },
    computed: {
        ...mapState("auth", ["user"]),
        filteredItems() {
            return this.items
        },
        canEditProducts() {
            return checkHasPermission({
                isAdmin: Boolean(this.user && this.user.isAdmin),
                permissionId: PERMISSION_CODES.CMS_EDIT_PRODUCT,
                userPermissionIds: (this.user && this.user.permissionIds) || [],
            })
        },
        canCreateProducts() {
            return checkHasPermission({
                isAdmin: Boolean(this.user && this.user.isAdmin),
                permissionId: PERMISSION_CODES.CMS_CREATE_PRODUCT,
                userPermissionIds: (this.user && this.user.permissionIds) || [],
            })
        },
        selectedVariations() {
            const selectedIds = new Set(this.selectedVariationIds)
            return this.items.filter((item) => selectedIds.has(item.id))
        },
        allVisibleVariationsSelected() {
            return this.items.length > 0 && this.items.every((item) => this.selectedVariationIds.includes(item.id))
        },
        activeAdvancedFilterCount() {
            return [this.selectedManufacturer, this.selectedSupplier, this.selectedStatus].filter(Boolean).length
        },
        hasActiveFilters() {
            return Boolean(
                this.searchStr ||
                this.selectedCategory ||
                this.selectedManufacturer ||
                this.selectedSupplier ||
                this.selectedStatus ||
                this.viewMode !== "products" ||
                this.sortBy !== "dateAdded"
            )
        },
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        getServerAssetUrl,
        async fetchCategories() {
            const res = await http.get("/item-groups")
            this.itemGroups = res.data.result
        },
        async fetchProducts() {
            this.loading = true
            this.loadError = ""
            this.clearVariationSelection()
            try {
                // Build query parameters
                const params = new URLSearchParams({
                    page: this.page.toString(),
                    limit: this.limit.toString(),
                    search: this.searchStr,
                    variants: this.viewMode === "variations" ? "true" : "false"
                })

                // Add filters if selected
                if (this.selectedCategory) params.append('item_group', this.selectedCategory)
                if (this.selectedManufacturer) params.append('manufacturer', this.selectedManufacturer)
                if (this.selectedSupplier) params.append('supplier', this.selectedSupplier)
                if (this.selectedStatus) params.append('status', this.selectedStatus)

                // Add sorting
                if (this.sortBy) params.append('sortBy', this.sortBy)
                if (this.sortOrder) params.append('sortOrder', this.sortOrder)

                const res = await http.get(`/items?${params.toString()}`)
                this.items = res.data.result
                this.totalPages = res.data.totalPages
                this.totalResults = res.data.totalResults
            } catch (err) {
                console.log("error in loading items", err)
                this.loadError = "Could not load products."
            }
            this.loading = false
        },

        // Load filter options from API
        async fetchFilterOptions() {
            this.loadingFilters = true
            try {
                const [manufacturersRes, suppliersRes, statusesRes] = await Promise.all([
                    http.get('/items/manufacturers'),
                    http.get('/items/suppliers'),
                    http.get('/items/status-options')
                ])

                this.manufacturers = manufacturersRes.data.result || []
                this.suppliers = suppliersRes.data.result || []
                this.statusOptions = statusesRes.data.result || this.statusOptions
            } catch (err) {
                console.log("error in loading filter options", err)
            }
            this.loadingFilters = false
        },

        // Apply filters and refresh data
        async applyFilters() {
            this.page = 1 // Reset to first page when filtering
            await this.fetchProducts()
        },

        // Reset all filters to default
        async resetFilters() {
            this.selectedManufacturer = ""
            this.selectedSupplier = ""
            this.selectedStatus = ""
            this.viewMode = "products"
            this.sortBy = "dateAdded"
            this.sortOrder = "desc"
            this.selectedCategory = ""
            this.searchStr = ""
            this.showMoreFilters = false
            this.page = 1
            await this.fetchProducts()
        },

        // Toggle between products and variations view
        async toggleViewMode() {
            this.clearVariationSelection()
            this.page = 1 // Reset pagination when switching view mode
            await this.fetchProducts()
        },
        toggleVariationSelection(item) {
            if (!item || !item.id) return
            this.selectedVariationIds = this.selectedVariationIds.includes(item.id)
                ? this.selectedVariationIds.filter((id) => id !== item.id)
                : [...this.selectedVariationIds, item.id]
        },
        toggleSelectVisible(event) {
            this.selectedVariationIds = event.target.checked ? this.items.map((item) => item.id) : []
        },
        clearVariationSelection() {
            this.selectedVariationIds = []
        },
        async handleVariationOperationCompleted() {
            this.clearVariationSelection()
            await this.fetchProducts()
        },
        debouncedSearch() {
            // Clear existing timer
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer)
            }

            // Set new timer for debounced search
            this.searchDebounceTimer = setTimeout(() => {
                this.page = 1 // Reset to first page when searching
                this.fetchProducts()
            }, 300) // 300ms delay
        },
        toggleMoreFilters() {
            this.showMoreFilters = !this.showMoreFilters
        },
        handleDocumentClick(event) {
            const moreFilters = this.$refs.moreFilters
            if (this.showMoreFilters && moreFilters && !moreFilters.contains(event.target)) {
                this.showMoreFilters = false
            }
        },
    },
    watch: {
        page() {
            this.fetchProducts()
        },
        searchStr() {
            this.debouncedSearch()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.products-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $sectionGap;

    .title-section {
        h2 {
            font-size: 20px;
            font-weight: 600;
            color: $primaryColor;
            margin: 0;

            .product-count {
                font-weight: normal;
                opacity: 0.7;
                font-size: 18px;
            }
        }

        h3 {
            font-size: 16px;
            font-weight: 600;
            color: $primaryColor;
            margin: 0 0 12px 0;
        }
    }

    .action-buttons {
        display: flex;
        gap: $sectionGap - 2px;

        button {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            padding: 6px 12px;
            border-radius: 6px;

            i {
                font-size: 16px;
            }
        }

        .btn-secondary {
            background-color: $lightHoverColor;
            border: 1px solid $lightBorderColor;
            color: $textColor;

            &:hover {
                background-color: darken($lightHoverColor, 5%);
            }
        }

        .btn-primary {
            background-color: $primaryColor;
            border: 1px solid darken($primaryColor, 5%);
            color: white;

            &:hover {
                background-color: darken($primaryColor, 5%);
            }
        }
    }
}

.products-content {
    width: 100%;

    .compact-filters-bar {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
        background-color: $lightCardBg;
        border: 1px solid $lightBorderColor;
        border-radius: $cardRadius;
        padding: 8px 10px;
        margin-bottom: $sectionGap;

        .filter-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
            min-width: 120px;

            &.search-item {
                flex: 2;
                min-width: 220px;
                max-width: none;
            }

            &.category-filter {
                flex: 1.4;
                min-width: 160px;
            }

            &.view-filter {
                min-width: 110px;
            }

            &.sort-filter {
                min-width: 135px;
            }

            &.reset-filter {
                min-width: auto;
            }

            label {
                font-size: 12px;
                font-weight: 500;
                color: $textColor;
                margin-bottom: 2px;
                white-space: nowrap;
            }

            .form-select {
                padding: 8px 10px;
                border: 1px solid $lightBorderColor;
                border-radius: 4px;
                font-size: 13px;
                background-color: white;
                cursor: pointer;
                min-height: 36px;

                &:focus {
                    border-color: $primaryColor;
                    box-shadow: 0 0 0 2px rgba($primaryColor, 0.1);
                    outline: none;
                }
            }

            .input-with-icon {
                position: relative;

                .search-icon {
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: $textColor;
                    font-size: 16px;
                    opacity: 0.7;
                }

                .search-input {
                    padding: 8px 10px 8px 32px;
                    border-radius: 4px;
                    border: 1px solid $lightBorderColor;
                    font-size: 13px;
                    min-height: 36px;
                    width: 100%;

                    &:focus {
                        border-color: $primaryColor;
                        box-shadow: 0 0 0 2px rgba($primaryColor, 0.1);
                        outline: none;
                    }
                }

                .clear-search {
                    position: absolute;
                    right: 8px;
                    top: 50%;
                    transform: translateY(-50%);
                    border: none;
                    background: none;
                    padding: 0;
                    cursor: pointer;
                    color: $textColor;
                    opacity: 0.5;

                    &:hover {
                        opacity: 0.8;
                    }

                    i {
                        font-size: 14px;
                    }
                }
            }

            .btn-reset {
                background-color: $lightHoverColor;
                border: 1px solid $lightBorderColor;
                padding: 8px 12px;
                border-radius: 4px;
                font-size: 13px;
                cursor: pointer;
                display: flex;
                align-items: center;
                gap: 4px;
                color: $textColor;
                transition: background-color 0.2s;
                min-height: 36px;
                white-space: nowrap;

                &:hover {
                    background-color: darken($lightHoverColor, 5%);
                }

                i {
                    font-size: 16px;
                }

                .reset-text {
                    @media (max-width: 768px) {
                        display: none;
                    }
                }
            }

            .filter-loading {
                display: flex;
                align-items: center;
                gap: 6px;
                color: $textColor;
                opacity: 0.7;
                font-size: 13px;
                min-height: 36px;

                .spinning {
                    animation: spin 1s linear infinite;
                }
            }
        }

        .more-filters-wrapper {
            position: relative;
            flex: 0 0 auto;
        }

        .btn-more-filters {
            min-height: 36px;
            padding: 7px 10px;
            display: inline-flex;
            align-items: center;
            gap: 5px;
            border: 1px solid $lightBorderColor;
            border-radius: 4px;
            background: white;
            color: $textColor;
            font-size: 13px;
            white-space: nowrap;
            cursor: pointer;

            &:hover,
            &.active {
                border-color: rgba($primaryColor, 0.5);
                background: rgba($primaryColor, 0.04);
            }

            &:focus {
                outline: none;
                border-color: $primaryColor;
                box-shadow: 0 0 0 2px rgba($primaryColor, 0.1);
            }

            .material-icons {
                font-size: 16px;
            }

            .chevron {
                opacity: 0.65;
            }
        }

        .filter-count {
            min-width: 19px;
            height: 19px;
            padding: 0 5px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;
            background: $primaryColor;
            color: white;
            font-size: 11px;
            font-weight: 600;
        }

        .more-filters-popover {
            position: absolute;
            top: calc(100% + 8px);
            right: 0;
            z-index: 30;
            width: 320px;
            padding: 14px;
            display: grid;
            gap: 12px;
            border: 1px solid $lightBorderColor;
            border-radius: 8px;
            background: white;
            box-shadow: 0 14px 32px rgba(0, 0, 0, 0.16);

            .filter-item {
                min-width: 0;
            }
        }
    }

    .item-container {
        max-height: 80vh;
        overflow-y: auto;
        background-color: $lightCardBg;
        border: 1px solid $lightBorderColor;
        border-radius: 8px;
        padding: 12px;

        &::-webkit-scrollbar {
            width: 4px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
        }

        .product-list-item-wrapper {
            margin-bottom: 10px;
            width: 100%;
        }
    }

    .product-items-grid {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @media (min-width: 992px) {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;

            .product-list-item-wrapper {
                margin-bottom: 0;
            }
        }

        @media (min-width: 1200px) {
            grid-template-columns: repeat(3, 1fr);
        }

        @media (min-width: 1600px) {
            grid-template-columns: repeat(4, 1fr);
        }

        @media (min-width: 1920px) {
            grid-template-columns: repeat(5, 1fr);
        }
    }
}

.variation-tools-shell {
    display: grid;
    gap: 8px;
    margin-bottom: 12px;
}

.select-page-control {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    width: fit-content;
    margin: 0;
    color: rgba($textColor, 0.78);
    font-size: 12px;
    cursor: pointer;

    input {
        width: 16px;
        height: 16px;
        margin: 0;
        accent-color: $primaryColor;
    }
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;

    .spinner-border {
        width: 36px;
        height: 36px;
        margin-bottom: 12px;
    }

    .loading-text {
        font-size: 14px;
        color: $textColor;
        opacity: 0.7;
    }
}

.empty-state {
    text-align: center;
    padding: 40px 0;

    .empty-icon {
        margin-bottom: 12px;

        i {
            font-size: 42px;
            color: rgba($textColor, 0.2);
        }
    }

    h3 {
        margin-bottom: 8px;
        color: $textColor;
        font-size: 18px;
    }

    p {
        color: rgba($textColor, 0.7);
        margin-bottom: 16px;
        font-size: 14px;
    }
}

.pagination-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* Use tokenized section spacing for consistent vertical rhythm */
    margin-top: $sectionGap;
    width: 100%;
    grid-column: 1 / -1;

    .pagination-info {
        font-size: 14px;
        color: rgba($textColor, 0.7);
    }
}

/* Animations */
@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Dark mode styles */
:deep(.dark) {
    .select-page-control {
        color: rgba($darkTextColor, 0.78);

        input { accent-color: $secondaryColorDark; }
    }

    .products-header {
        .title-section {
            h2, h3 {
                color: $secondaryColorDark;
            }
        }

        .action-buttons {
            .btn-secondary {
                background-color: rgba(255, 255, 255, 0.05);
                border-color: $darkBorderColor;
                color: $darkTextColor;

                &:hover {
                    background-color: rgba(255, 255, 255, 0.08);
                }
            }
        }
    }

    .products-content {
        .compact-filters-bar {
            background-color: $darkCardBg;
            border-color: $darkBorderColor;

            .filter-item {
                label {
                    color: $darkTextColor;
                }

                .form-select {
                    background-color: $darkCardBg;
                    border-color: $darkBorderColor;
                    color: $darkTextColor;

                    &:focus {
                        border-color: $secondaryColorDark;
                        box-shadow: 0 0 0 2px rgba($secondaryColorDark, 0.2);
                    }
                }

                .input-with-icon {
                    .search-icon, .clear-search {
                        color: $darkTextColor;
                    }

                    .search-input {
                        background-color: $darkCardBg;
                        border-color: $darkBorderColor;
                        color: $darkTextColor;

                        &:focus {
                            border-color: $secondaryColorDark;
                            box-shadow: 0 0 0 2px rgba($secondaryColorDark, 0.2);
                        }
                    }
                }

                .btn-reset {
                    background-color: rgba(255, 255, 255, 0.05);
                    border-color: $darkBorderColor;
                    color: $darkTextColor;

                    &:hover {
                        background-color: rgba(255, 255, 255, 0.08);
                    }
                }

                .filter-loading {
                    color: $darkTextColor;
                }
            }

            .btn-more-filters {
                background-color: $darkCardBg;
                border-color: $darkBorderColor;
                color: $darkTextColor;

                &:hover,
                &.active {
                    border-color: rgba($secondaryColorDark, 0.6);
                    background-color: rgba(255, 255, 255, 0.05);
                }
            }

            .more-filters-popover {
                background-color: $darkCardBg;
                border-color: $darkBorderColor;
            }
        }

        .item-container {
            background-color: $darkCardBg;
            border-color: $darkBorderColor;

            &::-webkit-scrollbar-thumb {
                background-color: rgba(255, 255, 255, 0.2);
            }
        }
    }

    .loading-container {
        .loading-text {
            color: $darkTextColor;
        }
    }

    .empty-state {
        .empty-icon i {
            color: rgba($darkTextColor, 0.2);
        }

        h3 {
            color: $darkTextColor;
        }

        p {
            color: rgba($darkTextColor, 0.7);
        }
    }

    .pagination-info {
        color: rgba($darkTextColor, 0.7);
    }
}

@media (max-width: 768px) {
    .products-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;

        .action-buttons {
            width: 100%;
            flex-wrap: wrap;
        }
    }

    .products-content {
        .compact-filters-bar {
            padding: 8px;
            gap: 8px;

            .filter-item {
                min-width: auto;
                flex: 1;

                &.search-item {
                    flex: 100%;
                    max-width: none;
                }

                label {
                    font-size: 11px;
                }

                .form-select {
                    font-size: 12px;
                    padding: 6px 8px;
                    min-height: 32px;
                }

                .input-with-icon .search-input {
                    font-size: 12px;
                    padding: 6px 8px 6px 28px;
                    min-height: 32px;
                }

                .btn-reset {
                    font-size: 12px;
                    padding: 6px 8px;
                    min-height: 32px;
                }

                .filter-loading {
                    font-size: 12px;
                    min-height: 32px;
                }
            }

            .category-filter {
                min-width: calc(50% - 4px);
            }

            .view-filter,
            .sort-filter {
                flex: 1;
            }

            .more-filters-wrapper {
                flex: 1;
            }

            .btn-more-filters {
                width: 100%;
                justify-content: center;
            }

            .more-filters-popover {
                position: fixed;
                top: auto;
                right: 12px;
                bottom: 12px;
                left: 12px;
                width: auto;
                max-height: calc(100vh - 24px);
                overflow-y: auto;
            }
        }

        .product-items-grid {
            grid-template-columns: repeat(1, 1fr) !important;
        }
    }
}

/* Optimize card spacing for products page */
:deep(.products-card .card-container) {
    padding: 16px;
}
</style>
